import Select from "common/Select/Select";
import { DefaultOptionType, SelectValue } from "antd/es/select";
import { FixMeLater } from "types";
import { useEffect } from "react";
import { DOLLAR_COINS } from "@shared/common/constants";
import { DEFAULT_PAIRS } from "features/portfolio/common/constants.ts";
import styled from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";

interface CoinSelectionDropdownProps {
  symbol?: string;
  setSymbol: (symbol: string) => void;
  pieChartData?: FixMeLater[];
  symbols?: string[];
  checkBlacklist: boolean;
  loading?: boolean;
  loaded?: boolean;
}

const DEFAULT_SYMBOL = "BTC/USDT";

const CoinSelectionDropdown = ({
  symbol,
  setSymbol,
  pieChartData,
  symbols,
  checkBlacklist = false,
  loading,
  loaded,
}: CoinSelectionDropdownProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const allSymbolsSet = new Set(symbols || []);

  const checkBlacklistSymbol = (symbol: string): boolean => {
    if (!checkBlacklist) return false;
    const [asset, pair] = symbol.split("/");
    return DOLLAR_COINS.includes(asset) && DOLLAR_COINS.includes(pair);
  };

  const checkValidSymbol = (symbol: string) => {
    const isValid = allSymbolsSet.has(symbol);
    return isValid && !checkBlacklistSymbol(symbol);
  };

  const getTopSymbols = () => {
    if (!pieChartData?.length) return [];
    const pieChartCoins = pieChartData
      .filter((dataPoint) => dataPoint.name !== "Other")
      .map((dataPoint) => dataPoint.name);
    let topSymbols: string[] = [];
    pieChartCoins.forEach((coin) => {
      if (coin.includes("/")) {
        topSymbols.push(coin);
      } else {
        topSymbols = topSymbols.concat(
          DEFAULT_PAIRS.map((pair) => coin + "/" + pair)
        );
      }
    });

    const validTopSymbols = topSymbols.filter((symbol) =>
      checkValidSymbol(symbol)
    );
    return Array.from(new Set(validTopSymbols));
  };

  const getOptions = (symbols: string[], isTopSymbol?: boolean) =>
    symbols.map((symbol) => ({
      label: symbol,
      value: symbol,
      key: `${symbol}${isTopSymbol && "_topSymbol"}`,
    }));

  const allSymbolOptions = getOptions(Array.from(allSymbolsSet));
  const topSymbols = getTopSymbols();
  const topSymbolOptions = getOptions(topSymbols, true);

  let options: DefaultOptionType[] = [];
  const showTopSymbolsGroup = topSymbolOptions?.length;
  if (showTopSymbolsGroup) {
    options.push({
      label: t("assets.my_top_symbols"),
      title: t("assets.my_top_symbols"),
      options: topSymbolOptions,
    });
  }

  if (allSymbolOptions?.length) {
    if (showTopSymbolsGroup) {
      options.push({
        label: t("assets.all_symbols"),
        title: t("assets.all_symbols"),
        options: allSymbolOptions,
      });
    } else {
      options = allSymbolOptions;
    }
  }

  useEffect(() => {
    if (!symbol) {
      if (topSymbols?.length) {
        setSymbol(topSymbols[0]);
      } else if (loaded) {
        setSymbol(DEFAULT_SYMBOL);
      }
    }
  }, [topSymbols]);

  return (
    <StyledSelect
      style={{ minWidth: 75, marginRight: 8 }}
      onChange={(value: SelectValue = "") => setSymbol(value.toString())}
      loading={loading}
      showSearch={true}
      value={symbol}
      popupMatchSelectWidth={200}
      virtual={true}
      variant="borderless"
      options={options}
      mobileTitle={t("assets.symbol")}
    />
  );
};

export default CoinSelectionDropdown;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    &:not(.ant-select-customize-input) {
      background: transparent;
    }
  }
`;
